const modeToUrl = {
    local: 'http://localhost:3000/api',
    development: 'https://api.qapline.k42.dev/api',
    // NOTE: since the cutover, api.qapline.com is the NEW production backend.
    // A `main` build therefore talks to the new backend, not the legacy v3 stack
    // it was written against - the legacy API is only reachable by IP now.
    main: 'https://api.qapline.com/api',
    // new production (mono codebase), Cloud Run behind its own load balancer.
    // Same host as `main` since the cutover - there is only one api.qapline.com,
    // and it now serves this environment.
    prod: 'https://api.qapline.com/api',
};

const modeToWebsocketUrl = {
    local: 'ws://localhost:3004',
    development: 'wss://api.qapline.k42.dev',
    main: 'wss://api.qapline.com',
    prod: 'wss://api.qapline.com',
};

export const api = (VITE_MODE?: string) =>
    ({
        //     development server
        // url: 'https://dev.qapline.api.koala42.com/api',

        //     local server
        //url: 'http://localhost:3000/api',

        //     production server
        // url: 'https://api.qapline.k42.app/api',

        //     auto server
        url:
            VITE_MODE && VITE_MODE in modeToUrl
                ? modeToUrl[VITE_MODE as keyof typeof modeToUrl]
                : modeToUrl.development,
        websocketUrl:
            VITE_MODE && VITE_MODE in modeToWebsocketUrl
                ? modeToWebsocketUrl[VITE_MODE as keyof typeof modeToWebsocketUrl]
                : modeToWebsocketUrl.development,
        //TODO: move mapbox token to env variable
        mapboxToken: 'pk.eyJ1IjoiaHVsZGFjeiIsImEiOiJjbGJ6M2l3d2cwd2hyM3FvNXM5M2twb2RlIn0.nVPfoiec06LkIocNYRPoGA',
        endpoints: {
            cashflow: {
                overview: 'v3/cashflow/overview',
                projection: 'v3/cashflow/projection',
                items: 'v3/cashflow/items',
                pendingEarlyPay: 'v3/cashflow/early-pay/pending',
                settings: 'v3/early-pay/settings',
            },
            auth: {
                login: 'v3/auth/login',
                logout: 'v3/auth/logout',
                refresh: 'v3/auth/refresh',
                requestResetPassword: 'v3/auth/request-password',
                verifyResetToken: 'v3/auth/reset-password/verify',
                resetPassword: 'v3/auth/reset-password',
            },
            country: {
                list: 'v3/country',
            },
            user: {
                get: 'v3/user/:userId',
                profilePicture: 'v3/user/:userId/profile-picture',
                deleteProfilePicture: 'v3/user/:userId/profile-picture',
                uploadProfilePicture: 'v3/user/:userId/profile-picture',
                update: 'v3/user/:userId',
                password: 'v3/user/:userId/password',
                list: 'v3/user/list',
            },
            customer: {
                list: 'v3/customer',
                get: 'v3/customer/:customerId',
                getSelf: 'v3/customer/self',
                update: 'v3/customer/:customerId',
                create: 'v3/customer',
                delete: 'v3/customer/:customerId',
                profilePicture: 'v3/customer/:customerId/profile-picture',
                registration: {
                    get: 'v3/customer/registration',
                    request: 'v3/customer/registration/request',
                    register: 'v3/customer/registration',
                },
            },
            carrier: {
                list: 'v3/carrier-new',
                create: 'v3/carrier-new',
                getList: 'v3/carrier-new/list',
                get: 'v3/carrier-new/:carrierId',
                exportCsv: 'v3/carrier-new/export/csv',
                customerRelation: 'v3/carrier-new/:carrierId/my-carriers',
                update: 'v3/carrier-new/:carrierId',
                delete: 'v3/carrier-new/:carrierId',
            },
            customerUser: {
                get: 'v3/customer/customer-user/:customerUserId',
                list: 'v3/customer/:customerId/customer-user',
                delete: 'v3/customer/customer-user/:customerUserId',
                create: 'v3/customer/:customerId/customer-user',
                invitation: {
                    check: 'v3/customer/customer-user/invitation/check',
                },
                register: 'v3/customer/customer-user/register',
            },
            customerContact: {
                get: 'v3/customer/customer-contact/:customerContactId',
                list: 'v3/customer/:customerId/customer-contact',
                delete: 'v3/customer/customer-contact/:customerContactId',
                create: 'v3/customer/:customerId/customer-contact',
                update: 'v3/customer/customer-contact/:customerContactId',
            },
            customerLocation: {
                get: 'v3/customer/location/:locationId',
                list: 'v3/customer/:customerId/location',
                update: 'v3/customer/location/:locationId',
                create: 'v3/customer/:customerId/location',
                delete: 'v3/customer/location/:locationId',
            },
            geocoding: {
                forward: 'v3/geocoding/forward',
                reverse: 'v3/geocoding/reverse',
            },
            rate: 'v3/rate',
            commission: {
                get: 'v3/commission-new/:commissionId',
                create: 'v3/commission-new',
                list: 'v3/commission-new',
                update: 'v3/commission-new/:commissionId',
                delete: 'v3/commission-new/:commissionId',
                complete: 'v3/commission-new/:commissionId/complete',
                summary: 'v3/commission-new/summary',
                exportCsv: 'v3/commission-new/export/csv',
                metrics: {
                    states: 'v3/commission-new/metrics/states',
                },
            },
            // auth
            // carrier
            carrierCSV: 'v3/carrier/csv',
            vehicleType: 'v3/vehicle_type',
            dispatcherVehicleFeature: 'dispatcher_vehicle_feature',
            dispatcherSearch: '/v3/dispatcher/commission/search',
            carrierInquiry: 'v3/carrier/enquiry',
            onboardingEmail: 'v3/carrier/sendOnboardingEmail',
            // invoices
            invoice: {
                invoicingStatusCounts: 'v3/invoice-new/invoicing-status-counts',
                invoicingStatuses: 'v3/invoice-new/invoicing-statuses',
                commissionsForInvoicing: 'v3/invoice-new/commissions-for-invoicing',
                numbers: 'v3/invoice-new/numbers',
                preview: 'v3/invoice-new/preview',
                exportXml: 'v3/invoice-new/xml',
                mail: (invoiceId: number) => `v3/invoice-new/${invoiceId}/mail`,
                cancel: (invoiceId: number) => `v3/invoice-new/${invoiceId}/cancel`,
                list: 'v3/invoice-new',
                create: 'v3/invoice-new',
                get: (invoiceId: number) => `v3/invoice-new/${invoiceId}`,
                update: (invoiceId: number) => `v3/invoice-new/${invoiceId}`,
                delete: (invoiceId: number) => `v3/invoice-new/${invoiceId}`,
                customerSelfPaymentsStatus: 'v3/invoice-new/customer-self/payments-status',
            },
            dispatcher: {
                get: 'v3/dispatcher-new/:dispatcherId',
                create: 'v3/dispatcher-new',
                update: 'v3/dispatcher-new/:dispatcherId',
                list: 'v3/dispatcher-new',
                delete: 'v3/dispatcher-new/:dispatcherId',
            },
            invoiceFiles: 'services/uploader.php',
            // customers
            customers: 'v3/customer',
            customersList: 'v3/customer/list',
            customersCSV: 'v3/customer/csv',
            // commissions
            commissionsList: 'v3/commission',
            commissionSummary: 'v3/commission/summary',
            commissionsCSV: 'v3/commission/csv',
            commissionListNumbers: 'v3/commission/numbers',
            // order
            orderCarrier: 'v3/order',
            orderCarrierPdf: 'v3/order/pdf',
            orderCarrierConfirmationEmail: 'v3/order/email',

            orderConfirmationEmail: 'v3/order/confirmationEmail',

            orderLoading: 'v3/loading',
            orderLoadingPdf: 'v3/loading/pdf',
            orderLoadingConfirmationEmail: 'v3/loading/email',

            orderDelivery: 'v3/delivery',
            orderDeliveryPdf: 'v3/delivery/pdf',
            orderDeliveryConfirmationEmail: 'v3/delivery/email',

            orderDischargePdf: 'v3/discharge/pdf',
            orderDischargeConfirmationEmail: 'v3/discharge/email',
            // dispatcher
            dispatchers: 'v3/dispatcher',
            checkDispatcherMail: 'v3/user/check-email',
            checkOnboardingToken: 'v3/dispatcher/checkToken',
            dispatcherEmail: 'v3/dispatcher/email',
            // enquiry
            enquiry: 'v3/enquiry',
            enquiryContact: 'v3/enquiry/contact',
            closeEnquiry: 'v3/enquiry/close',
            reopenEnquiry: 'v3/enquiry/reopen',
            // offer
            offer: 'v3/offer',
            // localization
            state: 'state/languages',
            // files
            files: 'v3/file',
            filesList: 'v3/file/list',
            // ares
            ares: 'v3/ares',

            sentryTunnel: 'v3/sentry/tunnel',
            attachments: {
                upload: 'v3/attachment/upload',
                checkUploadToken: 'v3/attachment/check-upload-token',
                generateUploadLink: 'v3/attachment/upload-link/:id',
                create: 'v3/attachment',
                list: 'v3/attachment',
                get: 'v3/attachment/:id',
                getFile: 'v3/attachment/file/:id',
                mailFile: 'v3/attachment/mail/:id',
                delete: 'v3/attachment/:id',
                update: 'v3/attachment/:id',
                mailAttachment: 'v3/attachment/mail/:id',
            },
            metrics: {
                get: 'v3/metrics',
            },
            expense: {
                list: 'v3/expense',
                create: 'v3/expense',
                get: (expenseId: number) => `v3/expense/${expenseId}`,
                update: (expenseId: number) => `v3/expense/${expenseId}`,
                delete: (expenseId: number) => `v3/expense/${expenseId}`,
                updateAmount: (expenseId: number) => `v3/expense/${expenseId}/amount`,
                override: (expenseId: number, year: number, month: number) =>
                    `v3/expense/${expenseId}/overrides/${year}/${month}`,
            },
            earlyPay: {
                settings: 'v3/early-pay/settings',
                dashboard: 'v3/early-pay/dashboard',
                overview: (commissionId: number) => `v3/early-pay/overview/${commissionId}`,
            },
        },
    }) as const;
